import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  BadgeCheck,
  BarChart3,
  Bot,
  FileText,
  LayoutDashboard,
  LineChart,
  LogOut,
  PieChart,
  Target,
  TrendingUp,
  Trophy,
  UserPlus,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  "https://backendhub.co.in/bcservice"
).replace(/\/+$/, "");

const localDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

function AdminDashboard() {
  const navigate = useNavigate();

  const [statsData, setStatsData] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    qualified: 0,
    proposal_sent: 0,
    won: 0,
    lost: 0,
  });

  const [allLeads, setAllLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const session = JSON.parse(
    sessionStorage.getItem("bc_admin_session") || "{}"
  );

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError("");

      const [statsResponse, leadsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/leads/stats/summary`),
        fetch(`${API_BASE_URL}/api/leads/`),
      ]);

      const statsJson = await statsResponse.json();
      const leadsJson = await leadsResponse.json();

      if (!statsResponse.ok) {
        throw new Error("Unable to load dashboard statistics.");
      }

      if (!leadsResponse.ok) {
        throw new Error("Unable to load lead analytics.");
      }

      setStatsData(statsJson);
      setAllLeads(leadsJson?.items || []);
    } catch (err) {
      console.error("Dashboard loading failed:", err);
      setError(err.message || "Unable to load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("bc_admin_session");
    navigate("/admin/login");
  };

  const conversionRate =
    statsData.total > 0
      ? Math.round((statsData.won / statsData.total) * 100)
      : 0;

  const qualificationRate =
    statsData.total > 0
      ? Math.round(
          ((statsData.qualified +
            statsData.proposal_sent +
            statsData.won) /
            statsData.total) *
            100
        )
      : 0;

  const activePipeline =
    statsData.new +
    statsData.contacted +
    statsData.qualified +
    statsData.proposal_sent;

  const sourceMetrics = useMemo(() => {
    let bot = 0;
    let form = 0;

    allLeads.forEach((lead) => {
      if (lead.acquisition?.source === "WEBSITE_BOT") {
        bot += 1;
      } else {
        form += 1;
      }
    });

    return { bot, form };
  }, [allLeads]);

  const projectMetrics = useMemo(() => {
    const counts = {};

    allLeads.forEach((lead) => {
      const project =
        lead.projectType || lead.project_type || "Other";
      counts[project] = (counts[project] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [allLeads]);

  const statusMetrics = [
    { label: "New", value: statsData.new, color: "#4f8cff" },
    { label: "Contacted", value: statsData.contacted, color: "#f6b94a" },
    { label: "Qualified", value: statsData.qualified, color: "#20c997" },
    { label: "Proposal", value: statsData.proposal_sent, color: "#8b7cf6" },
    { label: "Won", value: statsData.won, color: "#34d399" },
    { label: "Lost", value: statsData.lost, color: "#fb7185" },
  ];

  const statusDonut = useMemo(() => {
    if (!statsData.total) {
      return { background: "rgba(255,255,255,.12)" };
    }

    let start = 0;

    const segments = statusMetrics.map((item) => {
      const pct = (item.value / statsData.total) * 100;
      const from = start;
      start += pct;
      return `${item.color} ${from}% ${start}%`;
    });

    return {
      background: `conic-gradient(${segments.join(",")})`,
    };
  }, [statsData]);

  const sourceDonut = {
    background: statsData.total
      ? `conic-gradient(
          #4f8cff 0 ${(sourceMetrics.bot / statsData.total) * 100}%,
          #20c997 ${(sourceMetrics.bot / statsData.total) * 100}% 100%
        )`
      : "rgba(255,255,255,.12)",
  };

  const leadTrend = useMemo(() => {
    const days = [];

    for (let i = 6; i >= 0; i -= 1) {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() - i);

      days.push({
        key: localDateKey(date),
        label: date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        }),
        count: 0,
      });
    }

    allLeads.forEach((lead) => {
      const raw = lead.createdAt || lead.created_at;
      if (!raw) return;

      const key = localDateKey(new Date(raw));
      const day = days.find((item) => item.key === key);

      if (day) day.count += 1;
    });

    return days;
  }, [allLeads]);

  const trendPoints = useMemo(() => {
    const max = Math.max(...leadTrend.map((x) => x.count), 1);

    return leadTrend.map((item, index) => ({
      ...item,
      x: 40 + (index / Math.max(leadTrend.length - 1, 1)) * 610,
      y: 155 - (item.count / max) * 105,
    }));
  }, [leadTrend]);

  const polyline = trendPoints
    .map((point) => `${point.x},${point.y}`)
    .join(" ");

  const kpis = [
    { label: "Total Leads", value: statsData.total, icon: Users },
    { label: "New Leads", value: statsData.new, icon: UserPlus },
    { label: "Qualified", value: statsData.qualified, icon: BadgeCheck },
    { label: "Proposal Sent", value: statsData.proposal_sent, icon: FileText },
    { label: "Won", value: statsData.won, icon: Trophy },
    { label: "Conversion", value: `${conversionRate}%`, icon: TrendingUp },
    { label: "Qualification", value: `${qualificationRate}%`, icon: Target },
    { label: "Active Pipeline", value: activePipeline, icon: Activity },
  ];

  return (
    <div className="bc-dark-shell">
      <aside className="bc-dark-sidebar">
        <div className="bc-dark-brand">
          <div className="bc-dark-logo">BC</div>
          <div>
            <strong>Bairagi</strong>
            <span>Consultants</span>
          </div>
        </div>

        <nav className="bc-dark-nav">
          <button className="active">
            <LayoutDashboard size={17} />
            Dashboard
          </button>

          <button onClick={() => navigate("/admin/leads")}>
            <Users size={17} />
            Lead Management
          </button>
        </nav>

        <div className="bc-dark-profile">
          <div className="bc-dark-avatar">A</div>
          <div>
            <strong>Administrator</strong>
            <span>{session.email || "Admin"}</span>
          </div>
        </div>

        <button className="bc-dark-logout" onClick={handleLogout}>
          <LogOut size={16} />
          Logout
        </button>
      </aside>

      <main className="bc-dark-main">
        <header className="bc-dark-topbar">
          <div>
            <span className="bc-dark-eyebrow">ADMINISTRATION</span>
            <h1>Lead Dashboard</h1>
            <p>Live lead acquisition, pipeline and conversion performance.</p>
          </div>

          <button
            className="bc-dark-manage"
            onClick={() => navigate("/admin/leads")}
          >
            Manage Leads
          </button>
        </header>

        {loading && (
          <div className="bc-dark-message">Loading dashboard...</div>
        )}

        {error && (
          <div className="bc-dark-message error">
            <span>{error}</span>
            <button onClick={loadDashboard}>Retry</button>
          </div>
        )}

        <section className="bc-dark-kpis">
          {kpis.map((item) => {
            const Icon = item.icon;

            return (
              <article className="bc-dark-kpi" key={item.label}>
                <div>
                  <span>{item.label}</span>
                  <Icon size={15} />
                </div>
                <strong>{item.value}</strong>
              </article>
            );
          })}
        </section>

        <section className="bc-dark-dashboard-grid">
          <article className="bc-dark-panel bc-dark-trend">
            <div className="bc-dark-panel-head">
              <div>
                <span>ACQUISITION TREND</span>
                <h2>Leads — Last 7 Days</h2>
              </div>
              <LineChart size={18} />
            </div>

            <svg
              className="bc-dark-line-chart"
              viewBox="0 0 690 190"
              role="img"
              aria-label="Leads last seven days"
            >
              {[50, 85, 120, 155].map((y) => (
                <line
                  key={y}
                  x1="40"
                  y1={y}
                  x2="650"
                  y2={y}
                  className="bc-dark-grid-line"
                />
              ))}

              <polygon
                points={`40,158 ${polyline} 650,158`}
                className="bc-dark-area"
              />

              <polyline
                points={polyline}
                className="bc-dark-line"
              />

              {trendPoints.map((point) => (
                <g key={point.key}>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    className="bc-dark-point"
                  />

                  <text
                    x={point.x}
                    y="180"
                    textAnchor="middle"
                    className="bc-dark-axis"
                  >
                    {point.label}
                  </text>

                  {point.count > 0 && (
                    <text
                      x={point.x}
                      y={point.y - 10}
                      textAnchor="middle"
                      className="bc-dark-value"
                    >
                      {point.count}
                    </text>
                  )}
                </g>
              ))}
            </svg>
          </article>

          <article className="bc-dark-panel">
            <div className="bc-dark-panel-head">
              <div>
                <span>ACQUISITION</span>
                <h2>Lead Sources</h2>
              </div>
              <Bot size={18} />
            </div>

            <div className="bc-dark-donut-layout">
              <div className="bc-dark-donut" style={sourceDonut}>
                <div>
                  <strong>{statsData.total}</strong>
                  <span>Total</span>
                </div>
              </div>

              <div className="bc-dark-legend">
                <div>
                  <i className="dot bot" />
                  <span>Website BOT</span>
                  <strong>{sourceMetrics.bot}</strong>
                </div>
                <div>
                  <i className="dot form" />
                  <span>Contact Form</span>
                  <strong>{sourceMetrics.form}</strong>
                </div>
              </div>
            </div>
          </article>

          <article className="bc-dark-panel">
            <div className="bc-dark-panel-head">
              <div>
                <span>LEAD LIFECYCLE</span>
                <h2>Status Distribution</h2>
              </div>
              <PieChart size={18} />
            </div>

            <div className="bc-dark-donut-layout">
              <div className="bc-dark-donut" style={statusDonut}>
                <div>
                  <strong>{activePipeline}</strong>
                  <span>Active</span>
                </div>
              </div>

              <div className="bc-dark-legend">
                {statusMetrics.map((item) => (
                  <div key={item.label}>
                    <i
                      className="dot"
                      style={{ background: item.color }}
                    />
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <article className="bc-dark-panel">
            <div className="bc-dark-panel-head">
              <div>
                <span>PIPELINE</span>
                <h2>Lead Progress</h2>
              </div>
              <TrendingUp size={18} />
            </div>

            <div className="bc-dark-pipeline">
              {[
                ["New", statsData.new],
                ["Contacted", statsData.contacted],
                ["Qualified", statsData.qualified],
                ["Proposal Sent", statsData.proposal_sent],
                ["Won", statsData.won],
              ].map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <div>
                    <i
                      style={{
                        width: `${
                          statsData.total
                            ? (value / statsData.total) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
          </article>

          <article className="bc-dark-panel">
            <div className="bc-dark-panel-head">
              <div>
                <span>MARKET DEMAND</span>
                <h2>Project Mix</h2>
              </div>
              <BarChart3 size={18} />
            </div>

            <div className="bc-dark-projects">
              {projectMetrics.length === 0 ? (
                <p className="bc-dark-empty">No project data yet.</p>
              ) : (
                projectMetrics.map((project, index) => {
                  const pct = statsData.total
                    ? Math.round(
                        (project.count / statsData.total) * 100
                      )
                    : 0;

                  return (
                    <div key={project.name}>
                      <div>
                        <span>{project.name}</span>
                        <strong>{project.count}</strong>
                      </div>

                      <div className="bc-dark-project-track">
                        <i
                          className={`project-${index + 1}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </article>

          <article className="bc-dark-panel">
            <div className="bc-dark-panel-head">
              <div>
                <span>PERFORMANCE</span>
                <h2>Pipeline Health</h2>
              </div>
              <Activity size={18} />
            </div>

            <div className="bc-dark-health">
              <div>
                <TrendingUp size={17} />
                <strong>{conversionRate}%</strong>
                <span>Conversion Rate</span>
              </div>

              <div>
                <BadgeCheck size={17} />
                <strong>{qualificationRate}%</strong>
                <span>Qualification Rate</span>
              </div>

              <div>
                <Target size={17} />
                <strong>{activePipeline}</strong>
                <span>Active Pipeline</span>
              </div>
            </div>
          </article>
        </section>
      </main>
    </div>
  );
}

export default AdminDashboard;
